<template>
  <div class="md-layout">

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="blue">
        <template slot="header">
          <div class="card-icon">
            <md-icon>power</md-icon>
          </div>
          <p class="category">{{$lang.charts.voltage}}</p>
          <h3 class="title">
            <animated-number
              :value="averageVoltage"
              :formatValue="voltageFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.U.MIN"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.U.AVG"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.U.MAX"
                  :formatValue="voltageFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="rose">
        <template slot="header">
          <div class="card-icon">
            <i class="fas fa-bolt"></i>
          </div>
          <p class="category">{{$lang.charts.current}}</p>
          <h3 class="title">
            <animated-number
              :value="averageCurrent"
              :formatValue="currentFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.I.MIN"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.I.AVG"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.I.MAX"
                  :formatValue="currentFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="green">
        <template slot="header">
          <div class="card-icon">
            <md-icon>wb_incandescent</md-icon>
          </div>
          <p class="category">{{$lang.charts.active_power}}</p>
          <h3 class="title">
            <animated-number
              :value="activePower"
              :formatValue="activePowerFormat"
              :duration="animationDuration"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            <md-progress-spinner
              v-if="statisticData.loading"
              :md-diameter="15"
              :md-stroke="2"
              md-mode="indeterminate"
            ></md-progress-spinner>
            <span v-else>
              MIN
              <b>
                <animated-number
                  :value="statisticData.P.MIN"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | AVG
              <b>
                <animated-number
                  :value="statisticData.P.AVG"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              | MAX
              <b>
                <animated-number
                  :value="statisticData.P.MAX"
                  :formatValue="activePowerFormat"
                  :duration="animationDuration"
                />
              </b>
              {{$lang.general.per}} {{localizedDateRange()}}
            </span>
          </div>
        </template>
      </stats-card>
    </div>

    <div class="md-layout-item md-medium-size-50 md-xsmall-size-100 md-size-25">
      <stats-card header-color="warning">
        <template slot="header">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <p
            class="category"
          >{{$lang.charts.active_energy}} {{$lang.general.per}} {{localizedDateRange()}}</p>
          <h3 class="title">
            <animated-number
              :value="energyImport"
              :formatValue="energyImportFormat"
              :duration="1000"
            />
          </h3>
        </template>

        <template slot="footer">
          <div class="stats">
            {{$lang.charts.total_energy}}
            <animated-number
              v-if="energyOffset == 0"
              :value="statisticData.E_I.offsetted"
              :formatValue="energyImportFormat"
              :duration="1000"
            />

            <a
              v-else
              href="#"
              :title="$lang.general.merito_energy + ': ' + (statisticData.E_I.total / 1000).toFixed(2) + ' kWh'"
            >
              <i>
                <animated-number
                  :value="statisticData.E_I.offsetted"
                  :formatValue="energyImportFormat"
                  :duration="1000"
                />
              </i>
            </a>
          </div>
        </template>
      </stats-card>
    </div>

    <div
      v-for="graph in graphDraw"
      v-bind:key="graph.ref"
      class="md-layout-item md-medium-size-100 md-xsmall-size-100 md-size-50"
    >
      <md-card>
        <md-card-header class="md-card-header-icon md-card-header-warning">
          <div class="card-icon">
            <md-icon>equalizer</md-icon>
          </div>
          <h4 class="title">{{graph.title}}</h4>
        </md-card-header>
        <md-card-content>
          <highcharts class="charts" :options="graph.chartOptions" :ref="graph.ref"></highcharts>
        </md-card-content>
      </md-card>
    </div>

    <snack-bar ref="snackbar"></snack-bar>
  </div>
</template>

<script>
import snackBar from "@/components/SnackBar";
import AnimatedNumber from "animated-number-vue";
import { StatsCard } from "@/components";
import areaChartOptions from "@/config/charts/line";
import axios from "axios";
import format from "date-fns/format";
import start_of_today from "date-fns/start_of_today";
import end_of_today from "date-fns/end_of_today";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import startOfMonth from "date-fns/start_of_month";
import endOfMonth from "date-fns/end_of_month";
import startOfYear from "date-fns/start_of_year";
import endOfYear from "date-fns/end_of_year";


export default {
  components: {
    "snack-bar": snackBar,
    AnimatedNumber,
    StatsCard
  },

  data() {
    return {
      animationDuration: 500,
      phases: ["L1", "L2", "L3"],
      phasesUsed: 3,

      selectedDateRange: "",
      graphTypes: [],
      graphDraw: [],

      energyOffset: 0,

      averageVoltage: "",
      averageCurrent: "",
      activePower: "",
      energyImport: "",

      statisticData: {
        loading: false,
        U: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        I: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        P: {
          MIN: 0,
          AVG: 0,
          MAX: 0
        },
        E_I: {
          start: 0,
          total: 0,
          offsetted: 0
        }
      }
    };
  },

  computed: {
    WSmessage() {
      return this.$store.getters.getDataMessage;
    }
  },

  watch: {
    WSmessage: function(payload, oldPayload) {
      this.graphDraw.forEach(graph => {
        if (payload[graph.key]) {
          const { chart } = this.$refs[graph.ref][0];

          let chartDataLength = 0;
          const seriesLength = chart.series.length;
          for (let i = 0; i < seriesLength; i++) {
            if (chart.series[i].visible === true) {
              chartDataLength = chart.series[i].data.length;
              break;
            }
          }

          let shift = chartDataLength > 20;

          for (let i = 0; i < this.phasesUsed; i++) {
            chart.series[i].addPoint(
              [payload.ts * 1000, payload[graph.key][this.phases[i]]],
              false,
              shift,
              false
            );
          }

          chart.redraw();
        }
      });

      this.averageVoltage = 0;
      if (payload.U) {
        let dividerAVG = 0;
        Object.keys(payload.U).forEach(phase => {
          const value = payload.U[phase];
          if (value > 0) {
            this.averageVoltage += value;
            dividerAVG++;
          }
        });
        this.averageVoltage = this.averageVoltage / dividerAVG;
      }

      this.averageCurrent = 0;
      if (payload.I) {
        Object.keys(payload.I).forEach(phase => {
          const value = payload.I[phase];
          this.averageCurrent += value;
        });
      }

      this.activePower = 0;
      if (payload.P) {
        Object.keys(payload.P).forEach(phase => {
          const value = payload.P[phase];
          this.activePower += value;
        });
      }

      this.statisticData.E_I.total = 0;
      if (payload.E_I) {
        Object.keys(payload.E_I).forEach(phase => {
          const value = payload.E_I[phase];
          this.statisticData.E_I.total += value;
        });

        this.energyImport =
          this.statisticData.E_I.total - this.statisticData.E_I.start;
      } else {
        this.energyImport = 0;
      }

      if (!isNaN(this.energyOffset)) {
        this.statisticData.E_I.offsetted =
          this.statisticData.E_I.total + this.energyOffset * 1000;
      }
    }
  },

  created() {
    if (!this.$route.query.hasOwnProperty("graphs")) {
      //DEFAULT
      this.graphTypes = [3, 2];
    } else {
      try {
        this.graphTypes = JSON.parse(this.$route.query.graphs);
      } catch (err) {
        //DEFAULT
        this.graphTypes = [3, 2];
      }
    }

    if (this.$route.query.hasOwnProperty("period")) {
      switch (this.$route.query.period) {
        case "today":
          this.selectedDateRange = "today";
          break;
        case "week":
          this.selectedDateRange = "week";
          break;
        case "month":
          this.selectedDateRange = "month";
          break;
        case "year":
          this.selectedDateRange = "year";
          break;
        default:
          //DEFAULT
          this.selectedDateRange = "today";
          break;
      }
    } else {
      //DEFAULT
      this.selectedDateRange = "today";
    }

    this.initCharts();
    this.statsDate = this.getTSFromDateRange(this.selectedDateRange);
  },

  mounted() {
    this.getEnergyData();
    this.getStatisticsData();
  },

  methods: {
    initCharts() {
      this.graphDraw = [];

      this.graphTypes.forEach(type => {
        let chartOptions = JSON.parse(JSON.stringify(areaChartOptions));

        chartOptions.exporting.enabled = false;
        chartOptions.series = [];

        for (let i = 0; i < this.phasesUsed; i++) {
          chartOptions.series.push({
            name: this.$lang.charts.phase + " " + this.phases[i],
            type: "spline",
            visible: true,
            data: []
          });
        }

        let graphDrawData = {
          title: "",
          type: type,
          ref: "graph_" + type,
          chartOptions: chartOptions
        };

        switch (type) {
          case 1:
            graphDrawData.title = this.$lang.charts.realtime_voltage;
            graphDrawData.key = "U";
            graphDrawData.chartOptions.yAxis.labels.format = "{value} V";
            graphDrawData.chartOptions.tooltip.pointFormat =
              "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} V</b><br/>";
            break;
          case 2:
            graphDrawData.title = this.$lang.charts.realtime_current;
            graphDrawData.key = "I";
            graphDrawData.chartOptions.yAxis.labels.format = "{value} A";
            graphDrawData.chartOptions.tooltip.pointFormat =
              "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} A</b><br/>";
            break;
          case 3:
            graphDrawData.title = this.$lang.charts.realtime_active_power;
            graphDrawData.key = "P";
            graphDrawData.chartOptions.yAxis.labels.format = "{value} W";
            graphDrawData.chartOptions.tooltip.pointFormat =
              "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} W</b><br/>";
            break;
          case 4:
            graphDrawData.title = this.$lang.charts.realtime_reactive_power;
            graphDrawData.key = "Q";
            graphDrawData.chartOptions.yAxis.labels.format = "{value} VAr";
            graphDrawData.chartOptions.tooltip.pointFormat =
              "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} VAr</b><br/>";
            break;
          case 5:
            graphDrawData.title = this.$lang.charts.realtime_apparent_power;
            graphDrawData.key = "S";
            graphDrawData.chartOptions.yAxis.labels.format = "{value} VA";
            graphDrawData.chartOptions.tooltip.pointFormat =
              "<span style='color:{point.color}'>●</span> {series.name}: <b>{point.y} VA</b><br/>";
            break;
          default:
            graphDrawData.title = "Undefined";
        }

        this.graphDraw.push(graphDrawData);
      });
    },

    localizedDateRange() {
      switch (this.selectedDateRange) {
        case "today":
          return this.$lang.keys.today_stats;
        case "week":
          return this.$lang.keys.week;
        case "month":
          return this.$lang.keys.month;
        case "year":
          return this.$lang.keys.year;
      }
    },

    getTSFromDateRange(dateRange) {
      let statDate = {
        selector: dateRange,
        from: 0,
        to: 0
      };
      switch (dateRange) {
        case "today":
          statDate.from = format(start_of_today(), "X");
          statDate.to = format(end_of_today(), "X");
          break;
        case "week":
          statDate.from = format(
            startOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          statDate.to = format(
            endOfWeek(start_of_today(), {
              weekStartsOn: 1
            }),
            "X"
          );
          break;
        case "month":
          statDate.from = format(startOfMonth(start_of_today()), "X");
          statDate.to = format(endOfMonth(start_of_today()), "X");
          break;
        case "year":
          statDate.from = format(startOfYear(start_of_today()), "X");
          statDate.to = format(endOfYear(start_of_today()), "X");
          break;
      }
      return statDate;
    },

    getStatisticsData() {
      this.statisticData.loading = true;

      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/statistics",
        data: {
          token: this.$route.query.token,
          data: "U,I,P",
          statData: "MIN,MAX,AVG",
          phase: "CUM",
          from: this.statsDate.from,
          to: this.statsDate.to
        },
        withCredentials: true
      })
        .then(response => {
          if (response.data.status === "OK") {
            this.statisticData.U.MIN = response.data.data.U.CUM.MIN;
            this.statisticData.U.AVG = response.data.data.U.CUM.AVG;
            this.statisticData.U.MAX = response.data.data.U.CUM.MAX;

            this.statisticData.I.MIN = response.data.data.I.CUM.MIN;
            this.statisticData.I.AVG = response.data.data.I.CUM.AVG;
            this.statisticData.I.MAX = response.data.data.I.CUM.MAX;

            this.statisticData.P.MIN = response.data.data.P.CUM.MIN;
            this.statisticData.P.AVG = response.data.data.P.CUM.AVG;
            this.statisticData.P.MAX = response.data.data.P.CUM.MAX;

            this.statisticData.loading = false;
          }
        })
        .catch(error => {
          if (error.response.data.status == "FAIL") {
            this.$refs.snackbar.notify(
              this.$lang.messages.api_communication_error
            );
          }
        });
    },

    getEnergyData() {
      axios({
        method: "POST",
        url: this.$ENDPOINTS.API_R + "/profile",
        data: {
          token: this.$route.query.token,
          data: "E_I",
          phase: "L1,L2,L3",
          from: this.statsDate.from,
          to: Number(this.statsDate.from) + 60,
          cumulative: true
        },
        withCredentials: true
      })
        .then(response => {
          this.statisticData.E_I.start = response.data.data[0].E_I.cumulative;
        })
        .catch(error => {
          if (error.response) {
            if (error.response.data.code == 610) {
              this.statisticData.E_I.start = 0;
              this.$refs.snackbar.notify(
                this.$lang.messages.energy_not_found_for_selected_time
              );
            } else {
              this.$refs.snackbar.notify(
                this.$lang.messages.api_communication_error
              );
            }
          }
        });
    },

    voltageFormat(value) {
      return `${Number(value).toFixed(1)} V`;
    },

    currentFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kA`;
      } else if (value > 100) {
        return `${Number(value).toFixed(1)} A`;
      } else {
        return `${Number(value).toFixed(2)} A`;
      }
    },

    activePowerFormat(value) {
      if (value > 1000) {
        return `${Number(value / 1000).toFixed(2)} kW`;
      } else {
        return `${Number(value).toFixed(1)} W`;
      }
    },

    energyImportFormat(value) {
      if (value > 100000) {
        return `${Number(value / 1000).toFixed(2)} kWh`;
      } else {
        return `${Number(value).toFixed(2)} Wh`;
      }
    }

  }
};
</script>

<style scoped>
</style>
